import { useQueryClient } from '@tanstack/react-query';
import { useNavigate, useRouter, useRouterState } from '@tanstack/react-router';

import { logout, type MeOut, type SessionOut } from '@/lib/api/queries';
import { qk } from '@/lib/api/query-keys';
import { safeRedirect } from '@/lib/auth/redirect';
import { clearToken } from '@/lib/auth/token-store';
import { useMarket } from '@/lib/market/market-context';
import { appQueryPersister, clearUserCache } from '@/lib/query-persist';

/** `SessionOut.user`（UserOut）＋ 工作階段期限 → `MeOut`，不必等下一次 `/me` 才換掉 TopNav。 */
function meFromSession(session: SessionOut): MeOut | null {
  const user = session.user;
  if (user === null || user === undefined) return null;
  return {
    user_id: user.user_id,
    email: user.email,
    display_name: user.display_name ?? null,
    locale: user.locale,
    tier: user.tier,
    totp_enabled: user.totp_enabled === true,
    session_expires_at: session.expires_at,
    entitlements: user.entitlements ?? [],
  };
}

/**
 * 登入／註冊（含 TOTP 第二步）成功之後：
 *   1. `qk.me()` 立刻寫入這個人——TopNav 不必等一次來回才換成頭像；
 *   2. **全部** query invalidate——有些回應依工作階段而異（模型輸出的存取閘、盤前摘要），
 *      匿名時快取的那一份不能沿用；
 *   3. 回到 `?redirect=`（經過 `safeRedirect`，擋掉 open redirect），沒有就回儀表板。
 */
export function useCompleteSignIn(redirect: unknown): (session: SessionOut) => Promise<void> {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { market } = useMarket();
  return async (session) => {
    queryClient.setQueryData(qk.me(), meFromSession(session));
    void queryClient.invalidateQueries();
    await navigate({ href: safeRedirect(redirect, `/${market}/dashboard`), replace: true });
  };
}

/**
 * 工作階段在伺服器端已經不在了（登出、重設密碼）：記憶體與硬碟上的快取一起清掉，
 * iOS App 上連 Keychain 裡那張 token 也清掉（網站上是 no-op：cookie 由伺服器清）。
 * 登入那一側的 token 由 lib/api/queries.ts 的 login / register / verifyTotp 在回傳前存好。
 */
export function useForgetSession(): () => Promise<void> {
  const queryClient = useQueryClient();
  return async () => {
    await clearToken();
    await clearUserCache(queryClient, appQueryPersister());
    queryClient.setQueryData(qk.me(), null);
  };
}

/**
 * 登出：撤銷伺服器端的工作階段 → 清快取 → `qk.me()` 設成 null → 留在目前這一頁
 * （站內沒有「非登入不可」的頁面：自選股頁與盤前摘要在未登入時各自畫登入提示）。
 *
 * 登出失敗（網路斷了）也照樣清掉本機：使用者按下「登出」的意思是「這台電腦上不要再有我」。
 */
export function useSignOut(): () => Promise<void> {
  const router = useRouter();
  const forget = useForgetSession();
  const href = useRouterState({ select: (state) => state.location.href });
  return async () => {
    try {
      await logout();
    } catch (error) {
      console.warn('logout_failed', error);
    }
    await forget();
    // 重新跑一次目前這一頁的 loader 與渲染：被 clear() 拿掉的查詢在重新渲染時以匿名身分重抓。
    await router.navigate({ href, replace: true });
    await router.invalidate();
  };
}
